import { useCallback, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/contexts/OrganizationContext';
import { isUserEmailVerified } from '@/lib/auth';
import {
  GET_MY_SUPPLIER_CLAIM_CANDIDATE_RPC,
  parseSupplierClaimCandidate,
  type SupplierClaimCandidate,
} from '@/lib/supplierClaimCandidate';

export type SupplierClaimCandidateStatus = 'loading' | 'ready' | 'error';

export interface SupplierClaimCandidateView {
  candidate: SupplierClaimCandidate | null;
  status: SupplierClaimCandidateStatus;
  /** Re-read the candidate (the unknown state's Retry). */
  refetch: () => void;
}

export function supplierClaimCandidateQueryKey(userId: string | null) {
  return ['supplier-claim-candidate', userId] as const;
}

function readErrorField(error: unknown, field: 'code' | 'requestId'): string | null {
  if (typeof error !== 'object' || error === null || !(field in error)) return null;
  const value = (error as Record<string, unknown>)[field];
  return typeof value === 'string' && value ? value : null;
}

class SupplierClaimCandidateError extends Error {
  constructor(message: string, readonly errorCode: string, readonly requestId: string | null) {
    super(message);
    this.name = 'SupplierClaimCandidateError';
  }
}

async function fetchSupplierClaimCandidate(): Promise<SupplierClaimCandidate | null> {
  const { data, error } = await supabase.rpc(GET_MY_SUPPLIER_CLAIM_CANDIDATE_RPC);
  if (error) {
    const errorCode = readErrorField(error, 'code') ?? 'unknown';
    throw new SupplierClaimCandidateError(
      `${GET_MY_SUPPLIER_CLAIM_CANDIDATE_RPC} failed: ${error.message || errorCode}`,
      errorCode,
      readErrorField(error, 'requestId'),
    );
  }
  try {
    return parseSupplierClaimCandidate(data);
  } catch (parseError) {
    throw new SupplierClaimCandidateError(
      `${GET_MY_SUPPLIER_CLAIM_CANDIDATE_RPC} failed: ${(parseError as Error).message}`,
      'invalid_payload',
      null,
    );
  }
}

function captureCandidateError(error: unknown) {
  const errorCode = error instanceof SupplierClaimCandidateError ? error.errorCode : 'unknown';
  const requestId = error instanceof SupplierClaimCandidateError ? error.requestId : null;
  Sentry.captureException(error, {
    level: 'warning',
    tags: {
      feature: 'supplier_claim_offer',
      functionName: 'useSupplierClaimCandidate',
      rpcName: GET_MY_SUPPLIER_CLAIM_CANDIDATE_RPC,
      errorCode,
    },
    extra: {
      functionName: 'useSupplierClaimCandidate',
      rpcName: GET_MY_SUPPLIER_CLAIM_CANDIDATE_RPC,
      errorCode,
      requestId,
    },
    fingerprint: ['supplier-claim-offer', GET_MY_SUPPLIER_CLAIM_CANDIDATE_RPC, errorCode],
  });
}

/**
 * ENT-6459 (D1): the caller's supplier claim candidate from the server read model. Enabled only
 * for a signed-in user with a confirmed email. An error resolves to no candidate (never a false
 * offer) and is reported to Sentry with the canonical RPC payload.
 */
export function useSupplierClaimCandidate(): SupplierClaimCandidateView {
  const { user } = useOrganization();
  const userId = user?.id ?? null;
  const enabled = Boolean(userId) && isUserEmailVerified(user);

  const query = useQuery({
    queryKey: supplierClaimCandidateQueryKey(userId),
    enabled,
    staleTime: 60_000,
    retry: 1,
    queryFn: fetchSupplierClaimCandidate,
  });

  // Report the settled failure once (after the single retry), not once per attempt.
  const { error, errorUpdatedAt } = query;
  useEffect(() => {
    if (error) captureCandidateError(error);
  }, [error, errorUpdatedAt]);

  const { refetch: refetchQuery } = query;
  const refetch = useCallback(() => {
    void refetchQuery();
  }, [refetchQuery]);

  if (!enabled) return { candidate: null, status: 'ready', refetch };
  // A known answer (a candidate or none) stays known even if a later background refetch fails.
  if (query.data !== undefined) return { candidate: query.data, status: 'ready', refetch };
  // Fail closed on unknown: a first read in flight (or a retry after an error) is loading.
  if (query.isFetching || !query.isError) return { candidate: null, status: 'loading', refetch };
  return { candidate: null, status: 'error', refetch };
}
